import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import ClubConfigPanel from "./club/ClubConfigPanel";
import ClubCourtsPanel from "./club/ClubCourtsPanel";
import ClubReservationsPanel from "./club/ClubReservationsPanel";
import ClubTodayPanel, { type ClubPanelTab } from "./club/ClubTodayPanel";
import "./club/ClubPanel.css";
import { uploadAssetHref } from "../lib/assets";
import {
  membershipPaymentStatusLabel,
  membershipRequestStatusLabel,
} from "../lib/labels";
import { formatDate } from "../lib/dateFormat";
import { formatMoney } from "../lib/money";
import { readStoredClubId, storeClubId } from "../lib/clubSelection";
import { ClubScopeContext, scopedClubApi } from "../lib/clubScope";
import {
  api,
  ApiError,
  openAuthenticatedFile,
  type ClubCourt,
  type ClubInfo,
  type ClubMember,
  type ClubMembershipPaymentOrder,
  type ClubMembershipPaymentSettings,
  type PlayerClubApplication,
} from "../lib/api";

// Pestañas del panel (maqueta Club-Panel): todo lo que existía vive en una de ellas.
type Tab = ClubPanelTab;
const TAB_LABELS: Array<{ key: Tab; label: string }> = [
  { key: "hoy", label: "Hoy" },
  { key: "reservas", label: "Reservas" },
  { key: "socios", label: "Socios" },
  { key: "canchas", label: "Canchas y horarios" },
  { key: "config", label: "Configuración" },
];
const TAB_KEYS = TAB_LABELS.map((t) => t.key);


function clubRequestDateLabel(value: string | null): string {
  return formatDate(value, "Sin fecha");
}

export default function HomeClubPage() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("seccion") as Tab | null;
  const tab: Tab = requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : "hoy";
  const setTab = useCallback(
    (next: Tab) => {
      setSearchParams(next === "hoy" ? {} : { seccion: next }, { replace: true });
    },
    [setSearchParams],
  );
  const [reloadKey, setReloadKey] = useState(0);
  const isSuperadmin = Boolean(user?.roles.includes("superadmin"));
  const [club, setClub] = useState<ClubInfo | null>(null);
  const { askConfirm } = useAbortableConfirm([club?.id]);
  // Cuentas con más de un club: selector arriba de las pestañas. No hay club
  // «actual» global: cada petición lleva el club DE LOS DATOS que muestra
  // (ClubScopeContext / scopedClubApi), y `confirmedClubRef` es el club cuyos
  // datos están en pantalla.
  const [myClubs, setMyClubs] = useState<Array<{ id: number; name: string }>>([]);
  const confirmedClubRef = useRef<number | null>(null);
  const [courts, setCourts] = useState<ClubCourt[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [clubLogoFile, setClubLogoFile] = useState<File | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [membershipRequests, setMembershipRequests] = useState<PlayerClubApplication[]>([]);
  const [paymentSettings, setPaymentSettings] = useState<ClubMembershipPaymentSettings | null>(null);
  const [paymentOrders, setPaymentOrders] = useState<ClubMembershipPaymentOrder[]>([]);
  const [paymentForm, setPaymentForm] = useState({ membership_fee_amount: "0", currency: "MXN", payment_instructions: "" });
  const [newMember, setNewMember] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  // Club que se está abriendo desde el selector: mientras carga, el panel
  // queda inerte (ni mouse ni teclado) y ninguna acción se ejecuta.
  const [switchingTo, setSwitchingTo] = useState<number | null>(null);
  const switchingRef = useRef(false);
  // Cada carga lleva su número: una respuesta vieja nunca pinta encima.
  const loadSeq = useRef(0);

  /** Pide TODO lo de UN club (el que se pasa), sin depender de nada global. */
  const fetchClubData = useCallback(async (authToken: string, clubId: number | null) => {
    const capi = scopedClubApi(clubId);
    const [meResp, courtsResp, membersResp, requestsResp, paymentSettingsResp, paymentOrdersResp] = await Promise.all([
      capi.me(authToken),
      capi.listCourts(authToken),
      capi.listMembers(authToken),
      capi.listMembershipRequests(authToken),
      capi.getMembershipPaymentSettings(authToken),
      capi.listMembershipPaymentOrders(authToken),
    ]);
    return { meResp, courtsResp, membersResp, requestsResp, paymentSettingsResp, paymentOrdersResp };
  }, []);

  const applyClubData = useCallback((d: Awaited<ReturnType<typeof fetchClubData>>) => {
    confirmedClubRef.current = d.meResp.data.club.id;
    setMembers(d.membersResp.data.members);
    setMembershipRequests(d.requestsResp.data.requests);
    setPaymentSettings(d.paymentSettingsResp.data.settings);
    setPaymentOrders(d.paymentOrdersResp.data.orders);
    setPaymentForm({
      membership_fee_amount: String(d.paymentSettingsResp.data.settings.membership_fee_amount ?? 0),
      currency: d.paymentSettingsResp.data.settings.currency ?? "MXN",
      payment_instructions: d.paymentSettingsResp.data.settings.payment_instructions ?? "",
    });
    setClub(d.meResp.data.club);
    setCourts(d.courtsResp.data.courts);
    setReloadKey((k) => k + 1);
  }, []);

  /** Carga (o recarga) el club confirmado; la primera vez, el preferido. */
  const load = useCallback(async () => {
    if (!token) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    let target = confirmedClubRef.current ?? readStoredClubId(user?.id);
    try {
      try {
        const mine = await api.clubs.myClubs(token);
        const list = [...mine.data.clubs].sort((a, b) =>
          a.name.localeCompare(b.name, "es", { sensitivity: "base" }),
        );
        if (seq !== loadSeq.current) return;
        setMyClubs(list);
        if (target != null && !list.some((c) => c.id === target)) {
          // El club guardado ya no es suyo: volver al de siempre.
          storeClubId(user?.id, null);
          target = null;
        }
      } catch {
        // Sin la lista no se toca el club confirmado: se sigue en el mismo.
        if (seq === loadSeq.current) setError("No pudimos revisar tus clubes; sigues en el mismo club.");
      }
      let data: Awaited<ReturnType<typeof fetchClubData>>;
      try {
        data = await fetchClubData(token, target);
      } catch (err) {
        // Primera carga con un club guardado que ya no abre: el de siempre.
        if (confirmedClubRef.current === null && target !== null) {
          storeClubId(user?.id, null);
          data = await fetchClubData(token, null);
        } else {
          throw err;
        }
      }
      if (seq !== loadSeq.current) return;
      applyClubData(data);
    } catch (err) {
      if (seq === loadSeq.current) setError(err instanceof ApiError ? err.message : "No pudimos cargar tu club.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [token, user?.id, fetchClubData, applyClubData]);

  /** Cambiar de club: los datos (y con ellos el club de cada acción) pasan al
   * nuevo club SOLO si cargan; si falla, todo sigue en el anterior. */
  async function handleSelectClub(value: string) {
    if (!token || switchingRef.current) return;
    const next = Number(value) || null;
    if (next === null || next === confirmedClubRef.current) return;
    const seq = ++loadSeq.current;
    setMessage(null);
    setError(null);
    switchingRef.current = true;
    setSwitchingTo(next);
    try {
      const data = await fetchClubData(token, next);
      if (seq !== loadSeq.current) return;
      applyClubData(data);
      storeClubId(user?.id, data.meResp.data.club.id);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      const name = club?.name ?? "tu club";
      setError(
        `${err instanceof ApiError && err.message ? err.message : "No pudimos abrir ese club"}. Sigues en ${name}.`,
      );
    } finally {
      switchingRef.current = false;
      setSwitchingTo(null);
      if (seq === loadSeq.current) setLoading(false);
    }
  }

  /** Club sobre el que puede actuar una acción AHORA (el de los datos en
   * pantalla), o null si se está cambiando de club. */
  function actionClub(): number | null {
    if (switchingRef.current || !club || club.id !== confirmedClubRef.current) return null;
    return club.id;
  }

  /** ¿La respuesta de una acción iniciada en `clubId` aún corresponde a lo
   * que se ve? Si ya se cambió de club, no se repinta nada. */
  function stillOn(clubId: number): boolean {
    return !switchingRef.current && confirmedClubRef.current === clubId;
  }

  /** Avisos de los paneles hijos: solo si vienen del club que se ve. */
  function forClub<A extends unknown[]>(clubId: number, fn: (...args: A) => void) {
    return (...args: A) => {
      if (stillOn(clubId)) fn(...args);
    };
  }

  useEffect(() => {
    void load();
  }, [load]);

  function flashErr(err: unknown, fallback: string) {
    setError(err instanceof ApiError ? err.message || fallback : fallback);
    setMessage(null);
  }

  async function handleViewReceipt(receiptPath: string | null) {
    if (!token || !receiptPath) return;
    try {
      await openAuthenticatedFile(receiptPath, token);
    } catch (err) {
      flashErr(err, "No se pudo abrir el comprobante.");
    }
  }

  async function handleMembershipDecision(requestId: number, action: "approve" | "reject") {
    if (!token) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (action === "reject" && !(await askConfirm("¿Rechazar esta solicitud de membresía? El jugador no quedará como socio.", { acceptLabel: "Sí, rechazar solicitud", tone: "danger" }))) {
      return;
    }
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (action === "approve") {
        const response = await capi.approveMembershipRequest(token, requestId);
        if (!stillOn(cid)) return;
        setMessage(response.data.request.payment_order ? "Solicitud aprobada. Se generó una orden para que el jugador suba su comprobante." : "Solicitud aprobada. El jugador ya quedó como socio activo.");
      } else {
        await capi.rejectMembershipRequest(token, requestId);
        if (!stillOn(cid)) return;
        setMessage("Solicitud rechazada.");
      }
      if (stillOn(cid)) await load();
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo actualizar la solicitud.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePaymentSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await capi.updateMembershipPaymentSettings(token, {
        membership_fee_amount: Number(paymentForm.membership_fee_amount) || 0,
        currency: paymentForm.currency.trim().toUpperCase() || "MXN",
        payment_instructions: paymentForm.payment_instructions.trim() || null,
      });
      if (!stillOn(cid)) return;
      setPaymentSettings(response.data.settings);
      setMessage("Configuración de pagos guardada.");
      if (stillOn(cid)) await load();
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo guardar la configuración de pagos.");
    } finally {
      setSaving(false);
    }
  }

  async function handlePaymentOrderDecision(orderId: number, action: "confirm" | "reject") {
    if (!token) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    const confirmed = await askConfirm(action === "confirm" ? "¿Confirmar pago y activar al socio?" : "¿Rechazar el comprobante y pedir otro?", { acceptLabel: action === "confirm" ? "Sí, confirmar pago" : "Sí, rechazar comprobante", tone: action === "confirm" ? "default" : "danger" });
    if (!confirmed) return;
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (action === "confirm") {
        await capi.confirmMembershipPaymentOrder(token, orderId);
        if (!stillOn(cid)) return;
        setMessage("Pago confirmado. El jugador ya quedó como socio activo.");
      } else {
        await capi.rejectMembershipPaymentOrder(token, orderId);
        if (!stillOn(cid)) return;
        setMessage("Comprobante rechazado. El jugador podrá subir uno nuevo.");
      }
      if (stillOn(cid)) await load();
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo actualizar el pago.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !newMember.first_name.trim()) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await capi.addMember(token, {
        new_player: {
          first_name: newMember.first_name.trim(),
          last_name: newMember.last_name.trim() || null,
          email: newMember.email.trim() || null,
          phone: newMember.phone.trim() || null,
        },
      });
      if (!stillOn(cid)) return;
      setNewMember({ first_name: "", last_name: "", email: "", phone: "" });
      setMessage("Socio agregado.");
      if (stillOn(cid)) await load();
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo agregar al socio.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleMember(member: ClubMember) {
    if (!token) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (member.status === "active" && !(await askConfirm("¿Suspender a este socio? No podrá reservar canchas hasta reactivarlo.", { acceptLabel: "Sí, suspender socio", tone: "danger" }))) {
      return;
    }
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await capi.updateMemberStatus(
        token,
        member.id,
        member.status === "active" ? "suspended" : "active",
      );
      if (!stillOn(cid)) return;
      if (stillOn(cid)) await load();
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo actualizar al socio.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadClubLogo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !clubLogoFile) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await capi.uploadLogo(token, clubLogoFile);
      if (!stillOn(cid)) return;
      setClub(response.data.club);
      setClubLogoFile(null);
      setMessage("Logo del club guardado para credenciales.");
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo subir el logo del club.");
    } finally {
      setSaving(false);
    }
  }

  async function handleGenerateMemberCredential(member: ClubMember) {
    if (!token) return;
    const cid = actionClub();
    if (cid === null) return;
    const capi = scopedClubApi(cid);
    if (!stillOn(cid)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await capi.generateMemberCredential(token, member.id);
      if (!stillOn(cid)) return;
      setMembers((current) => current.map((item) => (item.id === member.id ? response.data.member : item)));
      setMessage(`Credencial generada para ${response.data.member.player.display_name ?? response.data.member.player.first_name ?? "socio"}.`);
    } catch (err) {
      if (!stillOn(cid)) return;
      flashErr(err, "No se pudo generar la credencial del socio.");
    } finally {
      setSaving(false);
    }
  }

  function assetHref(value: string | null | undefined) {
    return uploadAssetHref(value) ?? "";
  }

  async function handleCopyInvite() {
    if (!club) return;
    const url = `${window.location.origin}/club-invite/${club.slug}/${club.invite_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Link de invitación copiado. Compártelo por WhatsApp con tus socios.");
    } catch {
      setMessage(url);
    }
  }

  if (loading) {
    return (
      <section className="card">
        <p className="muted">Cargando tu club…</p>
      </section>
    );
  }
  if (!club) {
    return (
      <section className="card">
        <p className="form-error">{error ?? "No tienes un club asignado."}</p>
      </section>
    );
  }

  const pendingRequestCount = membershipRequests.filter((request) => request.status === "pending_review").length;
  const pendingPaymentCount = paymentOrders.filter((order) => order.status === "receipt_submitted").length;

  return (
    <div className="club-panel">
      {myClubs.length > 1 && (
        <div className="club-panel__clubbar">
          <label htmlFor="club-panel-club">Club</label>
          <select
            id="club-panel-club"
            className="t123-select"
            value={String(club.id)}
            disabled={switchingTo !== null}
            onChange={(e) => void handleSelectClub(e.target.value)}
          >
            {myClubs.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
          {switchingTo !== null && (
            <span className="club-panel__muted" role="status">
              Abriendo {myClubs.find((c) => c.id === switchingTo)?.name ?? "club"}…
            </span>
          )}
        </div>
      )}
      <ClubScopeContext.Provider value={club.id}>
      <fieldset
        className="club-panel club-panel__scope"
        aria-busy={switchingTo !== null}
        data-switching={switchingTo !== null}
        disabled={switchingTo !== null}
        {...(switchingTo !== null ? { inert: "" } : {})}
      >
      <nav className="club-panel__tabs" aria-label="Secciones del club">
        {TAB_LABELS.map((item) => {
          const count =
            item.key === "socios" ? pendingRequestCount + pendingPaymentCount : 0;
          return (
            <button
              key={item.key}
              type="button"
              className="club-panel__tab"
              aria-current={tab === item.key ? "page" : undefined}
              onClick={() => setTab(item.key)}
            >
              {item.label}
              {count > 0 && <span className="club-panel__tab-count">{count}</span>}
            </button>
          );
        })}
      </nav>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      {tab === "hoy" && token && (
        <ClubTodayPanel
          key={club.id}
          token={token}
          clubId={club.id}
          clubName={club.name}
          requests={membershipRequests}
          saving={saving}
          reloadKey={reloadKey}
          onGoTo={setTab}
          onMembershipDecision={(id, action) => void handleMembershipDecision(id, action)}
        />
      )}

      {tab === "reservas" && token && (
        <ClubReservationsPanel
          key={club.id}
          token={token}
          onMessage={forClub(club.id, (text: string) => {
            setError(null);
            setMessage(text);
          })}
          onChanged={forClub(club.id, () => setReloadKey((k) => k + 1))}
        />
      )}

      {tab === "config" && token && (
        <ClubConfigPanel
          key={club.id}
          token={token}
          club={club}
          isSuperadmin={isSuperadmin}
          rulesVersion={reloadKey}
          onSaved={forClub(club.id, (text: string) => {
            setError(null);
            setMessage(text);
          })}
          onClubUpdated={forClub(club.id, (updated: ClubInfo) => {
            if (updated.id === club.id) setClub(updated);
          })}
        />
      )}

      {tab === "canchas" && (
        <section className="club-panel__card">
          <div className="club-panel__card-head">
            <h2>Canchas y horarios</h2>
            <Link className="btn btn--compact btn--ghost" to="/admin">
              Panel de torneos →
            </Link>
          </div>
          <p className="club-panel__muted">Catálogo de canchas, estado y horario semanal de cada una.</p>
        </section>
      )}

      {tab === "socios" && (
        <>
          <section className="card">
            <div className="card__header-row">
              <div>
                <h2 className="card__title">Padrón de socios</h2>
                <p className="card__subtitle">
                  Registra socios o comparte el link de invitación para que se
                  unan solos desde su celular.
                </p>
              </div>
              <button
                type="button"
                className="btn btn--compact"
                onClick={() => void handleCopyInvite()}
              >
                Copiar link de invitación
              </button>
            </div>
            <form className="form" onSubmit={handleAddMember}>
              <label>
                Nombre
                <input
                  type="text"
                  required
                  value={newMember.first_name}
                  disabled={saving}
                  onChange={(e) =>
                    setNewMember({ ...newMember, first_name: e.target.value })
                  }
                />
              </label>
              <label>
                Apellido
                <input
                  type="text"
                  value={newMember.last_name}
                  disabled={saving}
                  onChange={(e) =>
                    setNewMember({ ...newMember, last_name: e.target.value })
                  }
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={newMember.email}
                  disabled={saving}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                />
              </label>
              <label>
                Teléfono
                <input
                  type="text"
                  value={newMember.phone}
                  disabled={saving}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                />
              </label>
              <button className="btn" type="submit" disabled={saving}>
                {saving ? "Agregando…" : "Agregar socio"}
              </button>
            </form>
          </section>

          <section className="card club-credential-card">
            <div className="card__header-row">
              <div>
                <h2 className="card__title">Credenciales del club</h2>
                <p className="card__subtitle">Sube el logo del club y genera una credencial para cada socio.</p>
              </div>
              {club?.logo_url ? (
                <img className="club-credential-logo-preview" src={assetHref(club.logo_url)} alt="Logo del club" />
              ) : null}
            </div>
            <form className="form" onSubmit={handleUploadClubLogo}>
              <label>
                Logo del club
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={saving}
                  onChange={(event) => setClubLogoFile(event.target.files?.[0] ?? null)}
                />
              </label>
              <button className="btn" type="submit" disabled={saving || !clubLogoFile}>
                {saving ? "Guardando…" : club?.logo_url ? "Actualizar logo" : "Subir logo"}
              </button>
            </form>
          </section>

          <section className="card">
            <h2 className="card__title">Socios ({members.length})</h2>
            {members.length === 0 ? (
              <p className="muted">
                Sin socios todavía. Agrégalos arriba o comparte el link de
                invitación.
              </p>
            ) : (
              <ul className="communications-feed">
                {members.map((member) => (
                  <li key={member.id} className="communications-feed__item">
                    <div className="communications-feed__meta">
                      <span
                        className={`status-pill${
                          member.status === "active" ? "" : " status-pill--dropped"
                        }`}
                      >
                        {member.status === "active" ? "Activo" : "Suspendido"}
                      </span>
                      <span className="muted">
                        {member.player.email ?? "Sin email"}
                        {member.player.phone ? ` · ${member.player.phone}` : ""}
                      </span>
                    </div>
                    <strong className="communications-feed__title">
                      {member.player.display_name ?? member.player.first_name}
                    </strong>
                    <div className="admin-home-actions">
                      <button
                        type="button"
                        className="btn btn--compact"
                        onClick={() => void handleGenerateMemberCredential(member)}
                        disabled={saving}
                      >
                        {member.credential_url ? "Regenerar credencial" : "Generar credencial"}
                      </button>
                      {member.credential_url ? (
                        <a className="btn btn--ghost btn--compact" href={assetHref(member.credential_url)} target="_blank" rel="noreferrer">
                          Ver credencial
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact"
                        onClick={() => void handleToggleMember(member)}
                        disabled={saving}
                      >
                        {member.status === "active" ? "Suspender" : "Reactivar"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {tab === "socios" && (
        <section className="card">
          <div className="card__header-row">
            <div>
              <p className="eyebrow">Nuevos socios</p>
              <h2 className="card__title">Solicitudes de inscripción</h2>
              <p className="card__subtitle">Aprueba o rechaza las solicitudes que llegan desde la pantalla Club del jugador.</p>
            </div>
          </div>
          {membershipRequests.length === 0 ? (
            <p className="muted">No hay solicitudes todavía.</p>
          ) : (
            <div className="club-membership-request-cards" aria-label="Solicitudes de inscripción">
              {membershipRequests.map((request) => {
                const playerName = request.player.display_name ?? request.player.first_name ?? `Perfil #${request.player_profile_id}`;
                const contact = request.player.phone ?? request.player.email ?? "Sin contacto";
                return (
                  <article className="club-membership-request-card" key={request.id}>
                    <div className="club-membership-request-card__main">
                      <div className="club-membership-request-card__avatar" aria-hidden="true">
                        {playerName.trim().slice(0, 1).toUpperCase()}
                      </div>
                      <div className="club-membership-request-card__identity">
                        <span className="club-membership-request-card__kicker">{request.source === "invite" ? "Entró con link de invitación" : "Solicitud de socio"}</span>
                        <h3>{playerName}</h3>
                        <a href={request.player.phone ? `tel:${request.player.phone}` : request.player.email ? `mailto:${request.player.email}` : undefined}>
                          {contact}
                        </a>
                      </div>
                      <span className={`status-pill club-request-status club-request-status--${request.status}`}>
                        {membershipRequestStatusLabel(request.status)}
                      </span>
                    </div>

                    <dl className="club-membership-request-card__details">
                      <div>
                        <dt>Enviada</dt>
                        <dd>{clubRequestDateLabel(request.created_at)}</dd>
                      </div>
                      <div>
                        <dt>Origen</dt>
                        <dd>{request.source === "invite" ? "Link de invitación" : "Clubes Torneos123"}</dd>
                      </div>
                      {request.payment_order ? (
                        <div>
                          <dt>Orden</dt>
                          <dd>#{request.payment_order.id} · {formatMoney(request.payment_order.amount, request.payment_order.currency)}</dd>
                        </div>
                      ) : null}
                    </dl>

                    {request.message ? (
                      <p className="club-membership-request-card__message">{request.message}</p>
                    ) : null}

                    <div className="club-membership-request-card__actions">
                      {request.status === "pending_review" ? (
                        <>
                          <button type="button" className="btn" disabled={saving} onClick={() => handleMembershipDecision(request.id, "approve")}>{saving ? "Procesando…" : "Aprobar solicitud"}</button>
                          <button type="button" className="btn btn--ghost" disabled={saving} onClick={() => handleMembershipDecision(request.id, "reject")}>Rechazar</button>
                        </>
                      ) : request.payment_order ? (
                        <span className="muted">Esperando seguimiento de la orden de pago.</span>
                      ) : (
                        <span className="muted">Solicitud revisada.</span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}


      {tab === "socios" && (
        <>
          <section className="card">
            <div className="card__header-row">
              <div>
                <p className="eyebrow">Membresías</p>
                <h2 className="card__title">Pagos manuales de membresía</h2>
                <p className="card__subtitle">Configura el costo que se cobra al aprobar una solicitud. Si el costo es 0, la aprobación activa al socio inmediatamente.</p>
              </div>
            </div>
            <form className="form" onSubmit={handleSavePaymentSettings}>
              <label>
                Precio de inscripción / membresía
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.membership_fee_amount}
                  disabled={saving}
                  onChange={(e) => setPaymentForm({ ...paymentForm, membership_fee_amount: e.target.value })}
                />
              </label>
              <label>
                Moneda
                <input
                  type="text"
                  maxLength={3}
                  value={paymentForm.currency}
                  disabled={saving}
                  onChange={(e) => setPaymentForm({ ...paymentForm, currency: e.target.value.toUpperCase() })}
                />
              </label>
              <label className="form__full-width">
                Instrucciones de pago
                <textarea
                  rows={4}
                  value={paymentForm.payment_instructions}
                  disabled={saving}
                  placeholder="Banco, CLABE, beneficiario, concepto, WhatsApp para dudas…"
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_instructions: e.target.value })}
                />
              </label>
              <button className="btn" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Guardar configuración"}
              </button>
              {paymentSettings && (
                <p className="muted">Actual: {formatMoney(paymentSettings.membership_fee_amount, paymentSettings.currency)}</p>
              )}
            </form>
          </section>

          <section className="card">
            <div className="card__header-row">
              <div>
                <h2 className="card__title">Órdenes de membresía</h2>
                <p className="card__subtitle">Valida comprobantes para activar al socio.</p>
              </div>
            </div>
            {paymentOrders.length === 0 ? (
              <p className="muted">Aún no hay órdenes de membresía.</p>
            ) : (
              <div className="club-membership-payment-cards" aria-label="Órdenes de membresía">
                {paymentOrders.map((order) => {
                  const playerName = order.player?.display_name ?? order.player?.first_name ?? `Perfil #${order.player_profile_id}`;
                  return (
                    <article className="club-membership-payment-card" key={order.id}>
                      <div className="club-membership-payment-card__header">
                        <div className="club-membership-payment-card__avatar" aria-hidden="true">
                          {playerName.trim().slice(0, 1).toUpperCase()}
                        </div>
                        <div className="club-membership-payment-card__identity">
                          <span>Orden #{order.id}</span>
                          <h3>{playerName}</h3>
                          <small>{clubRequestDateLabel(order.created_at)}</small>
                        </div>
                        <span className={`status-pill club-membership-payment-card__status club-membership-payment-card__status--${order.status}`}>
                          {membershipPaymentStatusLabel(order.status)}
                        </span>
                      </div>

                      <dl className="club-membership-payment-card__facts">
                        <div>
                          <dt>Monto</dt>
                          <dd>{formatMoney(order.amount, order.currency)}</dd>
                        </div>
                        <div>
                          <dt>Comprobante</dt>
                          <dd>
                            {order.receipt_url ? (
                              <button type="button" className="link-button" onClick={() => void handleViewReceipt(order.receipt_url)}>
                                Ver comprobante
                              </button>
                            ) : (
                              "Pendiente"
                            )}
                          </dd>
                        </div>
                      </dl>

                      <div className="club-membership-payment-card__actions">
                        {order.status === "receipt_submitted" ? (
                          <>
                            <button type="button" className="btn" disabled={saving} onClick={() => handlePaymentOrderDecision(order.id, "confirm")}>{saving ? "Procesando…" : "Confirmar pago"}</button>
                            <button type="button" className="btn btn--ghost" disabled={saving} onClick={() => handlePaymentOrderDecision(order.id, "reject")}>Rechazar comprobante</button>
                          </>
                        ) : (
                          <span className="muted">Sin acción pendiente.</span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {tab === "canchas" && (
        <ClubCourtsPanel
          key={club.id}
          token={token}
          courts={courts}
          onReload={async () => {
            if (stillOn(club.id)) await load();
          }}
          onNotice={forClub(club.id, (text: string) => {
            setError(null);
            setMessage(text);
          })}
          onFail={forClub(club.id, flashErr)}
        />
      )}
      </fieldset>
      </ClubScopeContext.Provider>
    </div>
  );
}
